/* Boost supplies matching handles and facets. Shopify Liquid owns every card. */
(() => {
  'use strict';
  const API = 'https://services.mybcapps.com/bc-sf-filter/';
  const baseName = name => name.replace(/\[\]$/, '');
  const isFacet = name => /^pf_[\w-]+(?:::[\w-]+)*$/.test(baseName(name));
  const pageNumber = value => Number.isFinite(Number(value)) ? Math.max(1, Math.floor(Number(value) || 1)) : 1;
  const valuesFor = (params, id) => [...params.getAll(id), ...params.getAll(`${id}[]`)];
  const activeOptions = options => (options || []).filter(option =>
    isFacet(option.filterOptionId || '') && option.status !== 'disabled');

  function purchaseOption(options) {
    return options.find(option => /purchase.?type|fulfillment/i.test(`${option.label} ${option.filterOptionId}`));
  }

  function filterMapping(config, options) {
    let custom = config.filterMap || {};
    if (typeof custom === 'string') custom = JSON.parse(custom);
    const mapping = { ...custom };
    const types = { vendor: 'filter.p.vendor', product_type: 'filter.p.product_type', tag: 'filter.p.tag' };
    options.forEach(option => {
      if (types[option.filterType]) mapping[types[option.filterType]] = option.filterOptionId;
      const metafield = /^metafield_::([\w-]+)::([\w-]+)$/.exec(option.filterType || '');
      if (metafield) mapping[`filter.p.m.${metafield[1]}.${metafield[2]}`] = option.filterOptionId;
    });
    const purchase = purchaseOption(options);
    if (purchase) mapping['filter.p.m.shop3d.fulfillment_types'] = purchase.filterOptionId;
    return mapping;
  }

  // Never send unknown parameters: Boost silently ignores unknown filter IDs.
  function normalizeFilters(params, options, mapping) {
    const result = new URLSearchParams(params);
    const known = new Set(options.map(option => option.filterOptionId));
    const price = options.find(option => option.filterType === 'price');
    const rangeKeys = ['filter.v.price.gte', 'filter.v.price.lte'];
    if (rangeKeys.some(key => result.has(key))) {
      if (!price) throw new Error('unsupported');
      const bounds = price.values || {};
      const min = result.get(rangeKeys[0]) || bounds.min || 0;
      const max = result.get(rangeKeys[1]) || bounds.max;
      if (max == null) throw new Error('unsupported');
      result.append(`${price.filterOptionId}[]`, `${min}:${max}`);
      rangeKeys.forEach(key => result.delete(key));
    }
    for (const [name, value] of [...result.entries()]) {
      if (name.startsWith('filter.')) {
        const id = mapping[name];
        if (!known.has(id)) throw new Error('unsupported');
        // Move all repeated values together before deleting their source key.
        if (result.has(name)) {
          const option = options.find(item => item.filterOptionId === id);
          result.getAll(name).forEach(selected => {
            const match = Array.isArray(option.values) && option.values.find(item =>
              String(item.key) === selected || String(item.label).toLowerCase() === selected.toLowerCase());
            result.append(`${id}[]`, match ? String(match.key) : selected);
          });
          result.delete(name);
        }
      } else if (name.startsWith('pf_')) {
        if (!isFacet(name) || !known.has(baseName(name))) throw new Error('unsupported');
      }
    }
    return result;
  }

  function requestUrl(config, params, metadata = false) {
    const query = metadata ? '' : (params.get('og_q') || '').trim();
    const url = new URL(API + (query ? 'search' : 'filter'));
    url.searchParams.set('shop', config.shop);
    url.searchParams.set('collection_scope', String(config.collectionId));
    url.searchParams.set('page', metadata ? '1' : String(pageNumber(params.get('page'))));
    url.searchParams.set('limit', metadata ? '1' : String(config.pageSize));
    url.searchParams.set('build_filter_tree', 'true');
    url.searchParams.set('event_type', query ? 'search' : 'filter');
    url.searchParams.set('sort', params.get('sort_by') || config.defaultSort || 'manual');
    url.searchParams.set('currency', config.currency);
    url.searchParams.set('country', config.country);
    if (query) url.searchParams.set('q', query);
    if (!metadata) {
      for (const [name, value] of params) {
        // Arrays are required even for a single price range on this store's API.
        if (isFacet(name)) url.searchParams.append(`${baseName(name)}[]`, value);
      }
    }
    return url;
  }

  async function readJson(url, signal) {
    const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result = await response.json();
    if (result.error || !Array.isArray(result.products) || !Number.isFinite(result.total_product)) {
      throw new Error('Invalid Boost response');
    }
    return result;
  }

  // Limit concurrent Liquid renders rather than issuing one burst per page.
  async function mapConcurrent(items, limit, callback) {
    const output = new Array(items.length);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        output[index] = await callback(items[index], index);
      }
    }));
    return output;
  }

  function element(tag, text, className) {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (className) node.className = className;
    return node;
  }

  class BoostCollection {
    constructor(section, config) {
      this.section = section;
      this.config = config;
      this.labels = config.labels;
      this.outer = section.querySelector('[data-productgrid-outer]');
      this.results = section.querySelector('[data-og-boost-results]');
      this.items = section.querySelector('[data-og-boost-items]');
      this.filters = section.querySelector('[data-og-boost-filters]');
      this.sidebar = section.querySelector('[data-og-boost-sidebar]');
      this.status = section.querySelector('[data-og-boost-status]');
      this.pagination = section.querySelector('[data-og-boost-pagination]');
      this.retry = section.querySelector('[data-og-boost-retry]');
      this.clear = section.querySelector('[data-og-boost-clear]');
      this.options = [];
      this.mapping = {};
      this.cache = new Map();
      this.items.querySelectorAll('[data-product-item]').forEach(card => {
        const url = card.getAttribute('data-product-quickshop-url') || '';
        const handle = url.split('?')[0].split('/products/')[1];
        if (handle) this.cache.set(handle, card.cloneNode(true));
      });
      this.sequence = 0;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      const owns = target => section.contains(target) || this.sidebar?.contains(target)
        || target.closest('[data-og-boost-owner]')?.dataset.ogBoostOwner === config.sectionId;
      document.addEventListener('change', event => { if (owns(event.target)) this.onChange(event); }, { signal });
      document.addEventListener('submit', event => { if (owns(event.target)) this.onSubmit(event); }, { signal });
      document.addEventListener('click', event => { if (owns(event.target)) this.onClick(event); }, { signal });
      window.addEventListener('popstate', () => this.refresh(), { signal });
      this.refresh();
    }

    navigate(params, replace = false) {
      const url = new URL(window.location.href);
      url.search = params.toString();
      window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
      return this.refresh();
    }

    clearFilters() {
      const params = new URLSearchParams(window.location.search);
      for (const name of [...params.keys()]) {
        if (name.startsWith('filter.') || name.startsWith('pf_') || name === 'og_q' || name === 'page') params.delete(name);
      }
      this.navigate(params);
    }

    onClick(event) {
      const clear = event.target.closest('[data-og-boost-clear]');
      if (clear) { event.preventDefault(); this.clearFilters(); return; }
      if (event.target.closest('[data-og-boost-retry]')) this.refresh();
      const heading = event.target.closest('[data-og-boost-group] > [data-filter-group-trigger]');
      if (heading) {
        event.preventDefault();
        const expanded = heading.getAttribute('aria-expanded') !== 'true';
        heading.setAttribute('aria-expanded', String(expanded));
        heading.parentElement.querySelector('[data-og-boost-group-content]').hidden = !expanded;
        return;
      }
      const page = event.target.closest('[data-og-boost-page]');
      const tab = event.target.closest('[data-og-boost-tab]');
      const facet = event.target.closest('a[data-og-boost-option], [data-og-boost-remove-filter]');
      if ((!page && !tab && !facet) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const url = new URL((page || tab || facet).href);
      this.navigate(url.searchParams);
    }

    onChange(event) {
      const target = event.target;
      if (target.closest('[data-og-boost-range]')) {
        window.clearTimeout(this.rangeTimeout);
        this.rangeTimeout = window.setTimeout(() => target.closest('form')?.requestSubmit(), 1000);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      if (target.matches('[data-og-boost-sort]')) {
        params.set('sort_by', target.value);
      } else if (target.matches('[data-og-boost-option]')) {
        const id = target.dataset.ogBoostOption;
        const option = this.options.find(item => item.filterOptionId === id);
        const previous = valuesFor(params, id).filter(value => value !== target.value);
        params.delete(id);
        params.delete(`${id}[]`);
        if (option.selectType !== 'single') previous.forEach(value => params.append(`${id}[]`, value));
        if (target.checked) params.append(`${id}[]`, target.value);
      } else return;
      params.delete('page');
      this.navigate(params);
    }

    onSubmit(event) {
      const form = event.target;
      const params = new URLSearchParams(window.location.search);
      if (form.matches('[data-og-boost-search]')) {
        const query = form.elements.og_q.value.trim();
        if (query) params.set('og_q', query);
        else params.delete('og_q');
      } else if (form.matches('[data-og-boost-range]')) {
        window.clearTimeout(this.rangeTimeout);
        const id = form.dataset.ogBoostRange;
        const min = form.elements.min.value;
        const max = form.elements.max.value;
        if (min !== '' && max !== '' && Number(min) > Number(max)) {
          form.elements.max.setCustomValidity('Enter a maximum greater than or equal to the minimum.');
          form.elements.max.reportValidity();
          form.elements.max.addEventListener('input', () => form.elements.max.setCustomValidity(''), { once: true });
          event.preventDefault();
          return;
        }
        params.delete(id);
        params.delete(`${id}[]`);
        if (min !== '' || max !== '') {
          const bounds = this.options.find(item => item.filterOptionId === id).values;
          params.set(`${id}[]`, `${min || bounds.min || 0}:${max || bounds.max}`);
        }
      } else return;
      event.preventDefault();
      params.delete('page');
      this.navigate(params);
    }

    async card(product, signal) {
      const handle = product.handle;
      if (typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) throw new Error('Invalid handle');
      if (this.cache.has(handle)) return this.cache.get(handle).cloneNode(true);
      const url = new URL(`${this.config.collectionUrl}/products/${handle}`, window.location.origin);
      url.searchParams.set('section_id', 'only-games-product-card');
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Card HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('[data-product-item]');
      if (!card) throw new Error('Missing theme product card');
      // Import only the Liquid card, never the product-page layout or app scripts.
      card.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
      if (this.cache.size >= 140) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(handle, card.cloneNode(true));
      return card;
    }

    lifecycle(type) {
      this.section.dispatchEvent(new CustomEvent(`shopify:section:${type}`, {
        bubbles: true, detail: { sectionId: this.config.sectionId, onlyGamesBoost: true }
      }));
    }

    async refresh() {
      const sequence = ++this.sequence;
      this.request?.abort();
      this.request = new AbortController();
      const { signal } = this.request;
      const request = this.request;
      const timeout = window.setTimeout(() => request.abort(), 30000);
      this.results.setAttribute('aria-busy', 'true');
      this.items.inert = true;
      this.items.hidden = true;
      this.pagination.hidden = true;
      this.status.textContent = this.labels.loading;
      this.status.classList.add('visually-hidden');
      this.retry.hidden = true;
      this.clear.hidden = ![...new URLSearchParams(window.location.search).keys()].some(name =>
        name.startsWith('pf_') || name.startsWith('filter.') || name === 'og_q');
      this.section.querySelectorAll('.collection-featured-products').forEach(shelf => {
        shelf.hidden = !this.clear.hidden;
      });
      try {
        let params = new URLSearchParams(window.location.search);
        if (!this.options.length) {
          const metadata = await readJson(requestUrl(this.config, params, true), signal);
          this.options = activeOptions(metadata.filter?.options);
          this.mapping = filterMapping(this.config, this.options);
          this.filterCurrency = metadata.meta?.currency || this.config.currency;
        }
        params = normalizeFilters(params, this.options, this.mapping);
        const response = await readJson(requestUrl(this.config, params), signal);
        const page = pageNumber(params.get('page'));
        const pages = Math.max(1, Math.ceil(response.total_product / this.config.pageSize));
        if (page > pages) {
          params.set('page', String(pages));
          this.navigate(params, true);
          return;
        }
        const cards = await mapConcurrent(response.products, 4, product => this.card(product, signal));
        if (sequence !== this.sequence || !this.section.isConnected) return;
        const currentUrl = new URL(window.location.href);
        currentUrl.search = params.toString();
        window.history.replaceState({}, '', currentUrl);
        // Reuse Empire's lifecycle to clean up old cards and bind quick-shop,
        // swatches, comparison, view mode and lazy images on the Liquid results.
        this.lifecycle('unload');
        this.items.replaceChildren(...cards);
        this.items.hidden = false;
        this.items.inert = false;
        this.renderFilters(response.filter?.options, params);
        this.renderPagination(params, pages, page);
        this.updateTabs(params);
        const search = this.section.querySelector('[data-og-boost-search] input');
        if (search) search.value = params.get('og_q') || '';
        const sort = this.section.querySelector('[data-og-boost-sort], [data-productgrid-trigger-sort]');
        if (sort) sort.value = params.get('sort_by') || this.config.defaultSort;
        this.renderActiveFilters(params);
        this.lifecycle('load');
        this.status.textContent = cards.length ? `${response.total_product.toLocaleString()} products` : this.labels.empty;
        this.status.classList.toggle('visually-hidden', cards.length > 0);
        this.retry.hidden = true;
        this.clear.hidden = true;
      } catch (error) {
        if (sequence !== this.sequence || !this.section.isConnected) return;
        // Fail closed: never show unfiltered physical products under a Digital URL.
        this.items.hidden = true;
        this.status.textContent = error.message === 'unsupported' ? this.labels.unsupported : this.labels.error;
        this.status.classList.remove('visually-hidden');
        this.retry.hidden = false;
        this.clear.hidden = false;
        if (!this.filters.querySelector('[data-og-boost-group]')) this.filters.replaceChildren(element('p', this.labels.error));
      } finally {
        window.clearTimeout(timeout);
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    renderFilters(current, params) {
      const focused = this.filters.contains(document.activeElement) ? document.activeElement : null;
      const focusId = focused?.dataset.ogBoostOption;
      const focusValue = focused?.value || focused?.dataset.value;
      const open = new Set([...this.filters.querySelectorAll('[aria-expanded="true"]')].map(heading => heading.parentElement.dataset.ogBoostGroup));
      const first = !this.filters.querySelector('[data-og-boost-group]');
      const fragment = document.createDocumentFragment();
      for (const option of this.options) {
        const id = option.filterOptionId;
        const latest = activeOptions(current).find(item => item.filterOptionId === id) || option;
        const group = element('div', null, 'collection-filters__filter-group');
        group.dataset.ogBoostGroup = id;
        group.dataset.ogBoostOwner = this.config.sectionId;
        group.dataset.filterGroup = '';
        const expanded = first ? !option.isCollapsePC : open.has(id);
        const heading = element('button', null, 'collection-filters__filter-group-heading productgrid--sidebar-button');
        heading.type = 'button';
        heading.dataset.filterGroupTrigger = '';
        heading.setAttribute('aria-expanded', String(expanded));
        heading.append(element('div', option.filterType === 'price' ? `${option.label} (${this.filterCurrency})` : option.label, 'collection-filters__filter-title'));
        const iconWrapper = element('div', null, 'collection-filters__filter-icon-wrapper');
        iconWrapper.tabIndex = -1;
        const icon = element('span', null, 'collection-filters__filter-icon');
        const chevron = this.section.querySelector('[data-og-boost-chevron]');
        if (chevron) icon.append(chevron.content.cloneNode(true));
        iconWrapper.append(icon);
        heading.append(iconWrapper);
        group.append(heading);
        const selected = valuesFor(params, id);
        if (option.displayType === 'range' && !Array.isArray(option.values)) {
          const form = element('form', null, 'collection-filters__price-range-form');
          form.dataset.ogBoostRange = id;
          form.dataset.ogBoostGroupContent = '';
          form.hidden = !expanded;
          const fields = element('div', null, 'collection-filters__filter collection-filters__filter-range');
          const range = (selected[0] || '').split(':');
          ['min', 'max'].forEach((name, index) => {
            const wrapper = element('div', null, `collection-filters__filter-range--${name === 'min' ? 'from' : 'to'}`);
            const inputId = `og-boost-${this.config.sectionId}-${id}-${name}`;
            const label = element('label', name === 'min' ? 'From' : 'To', 'form-field-title collection-filters__filter-range-label');
            label.htmlFor = inputId;
            const input = element('input', null, 'collection-filters__filter-range-input form-field-input');
            input.id = inputId;
            input.type = 'number';
            input.name = name;
            input.step = 'any';
            input.min = '0';
            input.placeholder = String(option.values[name] ?? '');
            input.value = range[index] || '';
            if (option.filterType === 'price') {
              const symbol = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.filterCurrency }).formatToParts(0).find(part => part.type === 'currency')?.value;
              wrapper.append(element('span', symbol || this.filterCurrency, 'collection-filters__filter-range-currency'));
            }
            wrapper.append(input, label);
            fields.append(wrapper);
          });
          form.append(fields);
          const apply = element('button', this.labels.apply, 'visually-hidden');
          apply.type = 'submit';
          form.append(apply);
          group.append(form);
        } else {
          const list = element('ul', null, 'collection-filters__filter collection-filters__filter-list');
          list.dataset.ogBoostGroupContent = '';
          list.hidden = !expanded;
          const values = new Map();
          (Array.isArray(latest.values) ? latest.values : []).forEach(value => {
            const key = String(value.key ?? value.value ?? value);
            values.set(key, String(value.label ?? key));
          });
          selected.forEach(value => { if (!values.has(value)) values.set(value, value); });
          for (const [value, text] of values) {
            const item = element('li', null, 'collection-filters__filter-list-item filter-item');
            const checked = selected.includes(value);
            item.classList.toggle('collection-filters__filter-list-item--active', checked);
            item.dataset.filterActive = String(checked);
            const label = element('a', null, 'collection-filters__filter-link');
            if (checked) label.dataset.filterActive = '';
            label.dataset.ogBoostOption = id;
            label.dataset.value = value;
            const next = new URL(window.location.href);
            next.search = params.toString();
            const remaining = selected.filter(selection => selection !== value);
            next.searchParams.delete(id);
            next.searchParams.delete(`${id}[]`);
            if (option.selectType !== 'single') remaining.forEach(selection => next.searchParams.append(`${id}[]`, selection));
            if (!checked) next.searchParams.append(`${id}[]`, value);
            next.searchParams.delete('page');
            label.href = next.href;
            const checkbox = element('span', null, 'filter-icon--checkbox');
            checkbox.dataset.handle = `${id}-${list.children.length}`;
            const checkmark = this.section.querySelector('[data-og-boost-checkmark]');
            if (checkmark) {
              checkbox.append(checkmark.content.cloneNode(true));
              checkbox.querySelectorAll('[data-animation-state]').forEach(node => node.dataset.animationState = checked ? 'checked' : 'unchecked');
            }
            label.append(checkbox, element('span', text, 'collection-filters__filter-list-item-text'));
            item.append(label);
            list.append(item);
          }
          group.append(list);
        }
        fragment.append(group);
      }
      this.filters.replaceChildren(fragment);
      if (focusId) {
        [...this.filters.querySelectorAll('[data-og-boost-option]')]
          .find(input => input.dataset.ogBoostOption === focusId && (input.value || input.dataset.value) === focusValue)?.focus();
      }
    }

    renderPagination(params, pages, page) {
      const list = element('ul', null, 'pagination--inner');
      const link = (number, text, direction) => {
        const item = element('li', null, direction ? `pagination--${direction}` : null);
        const anchor = element('a', text, 'pagination--item');
        const url = new URL(window.location.href);
        url.search = params.toString();
        url.searchParams.set('page', String(number));
        anchor.href = url.href;
        anchor.dataset.ogBoostPage = '';
        anchor.setAttribute('aria-label', direction ? `Go to ${direction} page` : `Go to page ${number}`);
        if (direction) {
          const chevron = element('span', null, `pagination--chevron-${direction === 'previous' ? 'left' : 'right'}`);
          chevron.setAttribute('aria-hidden', 'true');
          const template = this.section.querySelector('[data-og-boost-chevron]');
          if (template) chevron.append(template.content.cloneNode(true));
          if (direction === 'previous') anchor.prepend(chevron);
          else anchor.append(chevron);
        }
        item.append(anchor);
        list.append(item);
      };
      if (page > 1) link(page - 1, this.labels.previous, 'previous');
      const numbers = [...new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2])]
        .filter(number => number > 0 && number <= pages).sort((a, b) => a - b);
      let previous = 0;
      for (const number of numbers) {
        if (previous && number > previous + 1) {
          const gap = element('li', null, 'pagination--ellipsis');
          gap.append(element('span', '…', 'pagination--item'));
          list.append(gap);
        }
        if (number === page) {
          const current = element('li', null, 'pagination--active');
          current.setAttribute('aria-label', `Page ${number}`);
          current.setAttribute('aria-current', 'true');
          current.append(element('span', String(number), 'pagination--item'));
          list.append(current);
        } else link(number, String(number));
        previous = number;
      }
      if (page < pages) link(page + 1, this.labels.next, 'next');
      this.pagination.replaceChildren(list);
      this.pagination.hidden = pages < 2;
    }

    renderActiveFilters(params) {
      const list = element('ul', null, 'active-filter-group');
      list.dataset.ogBoostOwner = this.config.sectionId;
      let count = 0;
      for (const option of this.options) {
        const id = option.filterOptionId;
        for (const value of valuesFor(params, id)) {
          count++;
          const item = element('li', null, 'active-filter-group__item-wrapper');
          const anchor = element('a', null, 'active-filter-group__item');
          const next = new URL(window.location.href);
          next.search = params.toString();
          next.searchParams.delete(id);
          next.searchParams.delete(`${id}[]`);
          valuesFor(params, id).filter(selected => selected !== value).forEach(selected => next.searchParams.append(`${id}[]`, selected));
          next.searchParams.delete('page');
          anchor.href = next.href;
          anchor.dataset.ogBoostRemoveFilter = '';
          const entry = Array.isArray(option.values) && option.values.find(entry => String(entry.key) === value);
          const label = option.displayType === 'range' ? `${value.replace(':', ' – ')}${option.filterType === 'price' ? ` ${this.filterCurrency}` : ''}` : entry?.label || value;
          anchor.append(element('span', label, 'active-filter-text'));
          const remove = this.section.querySelector('[data-og-boost-remove]');
          if (remove) anchor.append(remove.content.cloneNode(true));
          item.append(anchor);
          list.append(item);
        }
      }
      if (count) {
        const item = element('li', null, 'active-filter-group--remove-all filter-item--grid-simple');
        const clear = element('a', this.labels.clear, 'filter-item-clear-all');
        clear.href = this.config.collectionUrl;
        clear.dataset.ogBoostClear = '';
        item.append(clear);
        list.append(item);
      }
      for (const target of [this.section.querySelector('[data-og-boost-active]'), this.sidebar?.querySelector('[data-og-boost-active-mobile]')]) {
        if (!target) continue;
        target.replaceChildren(list.cloneNode(true));
        target.hidden = count === 0;
      }
      const title = `${this.labels.filters}${count ? ` (${count})` : ''}`;
      const heading = this.sidebar?.querySelector('.productgrid--sidebar-title');
      if (heading) heading.textContent = title;
      const trigger = this.section.querySelector('[data-productgrid-trigger-filters]');
      if (trigger) {
        trigger.setAttribute('aria-label', title);
        trigger.querySelector('.utils-filter-text').textContent = title;
      }
    }

    updateTabs(params) {
      const purchase = purchaseOption(this.options);
      const selected = purchase ? valuesFor(params, purchase.filterOptionId) : [];
      const active = selected.length === 1 && /^(Physical|Digital)$/i.test(selected[0]) ? selected[0].toLowerCase() : 'all';
      this.outer.dataset.ogBoostCatalogTab = active;
      const tabs = this.section.querySelector('[data-og-collection-tabs]');
      if (tabs) {
        for (const kind of ['all', 'physical', 'digital']) {
          let tab = tabs.querySelector(`[data-og-collection-tab="${kind}"]`);
          const value = Array.isArray(purchase?.values) ? purchase.values.find(item => String(item.label || item.key).toLowerCase() === kind) : null;
          if (!tab && value) {
            tab = element('a', this.labels[kind], 'og-tabs__button');
            tab.dataset.ogCollectionTab = kind;
            tabs.insertBefore(tab, tabs.querySelector('[data-og-collection-tab="profile"]'));
          }
          if (!tab) continue;
          tab.querySelector('.og-tabs__badge')?.remove();
          const url = new URL(window.location.href);
          if (purchase) {
            url.searchParams.delete(purchase.filterOptionId);
            url.searchParams.delete(`${purchase.filterOptionId}[]`);
            if (value && kind !== 'all') url.searchParams.set(`${purchase.filterOptionId}[]`, String(value.key));
          }
          url.searchParams.delete('page');
          url.hash = 'collection-products';
          tab.href = url.href;
          tab.dataset.ogBoostTab = '';
          const isActive = active === kind && !['#creator-profile', '#creator-posts'].includes(window.location.hash);
          tab.classList.toggle('is-active', isActive);
          if (isActive) tab.setAttribute('aria-current', 'page');
          else tab.removeAttribute('aria-current');
        }
      }
      // The curated shelf is not part of the filtered result set.
      this.section.querySelectorAll('.collection-featured-products').forEach(shelf => {
        shelf.hidden = [...params.keys()].some(name => isFacet(name) || name === 'og_q');
      });
    }

    destroy() {
      this.sequence++;
      this.request?.abort();
      this.listeners.abort();
      window.clearTimeout(this.rangeTimeout);
    }
  }

  // Export pure adapters for regression tests without starting a storefront.
  if (typeof document === 'undefined' && typeof module !== 'undefined' && module.exports) {
    module.exports = { requestUrl, normalizeFilters, filterMapping, activeOptions, mapConcurrent, pageNumber, BoostCollection };
    return;
  }
  function initialize(scope = document) {
    scope.querySelectorAll('[data-og-boost-config]').forEach(script => {
      const section = script.closest('.shopify-section');
      if (!section || section.ogBoostCollection) return;
      section.ogBoostCollection = new BoostCollection(section, JSON.parse(script.textContent));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => {
    if (!event.detail?.onlyGamesBoost) initialize(event.target);
  });
  document.addEventListener('shopify:section:unload', event => {
    if (event.detail?.onlyGamesBoost) return;
    event.target.ogBoostCollection?.destroy();
    delete event.target.ogBoostCollection;
  });
})();
